import type { ReactNode } from 'react';
import { useTranslation } from '../i18n';

interface DefaultErrorFallbackProps {
  reset: () => void;
}

/**
 * Default fallback rendered by `<ErrorBoundary>` when no custom fallback
 * is provided. Uses `t()` from react-i18next, so it MUST be mounted below
 * an `<I18nProvider>` in the tree.
 */
export function DefaultErrorFallback({ reset }: DefaultErrorFallbackProps): ReactNode {
  const { t, dir } = useTranslation();
  return (
    <div
      dir={dir}
      role="alert"
      data-testid="error-boundary-fallback"
      className="bg-background text-foreground flex min-h-screen items-center justify-center p-6"
    >
      <div className="rounded-card border-border w-full max-w-md border bg-white p-6 text-start shadow-lg dark:bg-gray-950">
        <h1 className="font-display text-foreground mb-2 text-lg font-semibold">
          {t('errors.title')}
        </h1>
        <p className="text-muted-foreground mb-4 text-sm">{t('errors.description')}</p>
        <button
          type="button"
          onClick={reset}
          data-testid="error-boundary-retry"
          className="rounded-btn bg-primary text-primary-foreground focus-visible:ring-ring px-4 py-2 text-sm font-medium transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t('errors.retry')}
        </button>
      </div>
    </div>
  );
}
